import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchChatMessageReactionDetails,
  toggleChatMessageReaction as postToggleChatMessageReaction,
} from './chatMessageReactionsApi';
import {
  buildReactionSummaryFromDetailGroups,
  normalizeReactionMessageId,
  patchMessageReactionSummaries,
} from './chatMessageReactionUtils';

const resolveRoomKey = ({ mode, scope, artistId, eventId, threadId, enabled }) => {
  if (!enabled) return '';
  if (scope === 'group') return threadId ? `group:${mode}:${threadId}` : '';
  if (scope === 'event') return eventId ? `event:${mode}:${eventId}` : '';
  if (mode === 'artist') return 'individual:artist:self';
  return artistId ? `individual:admin:${artistId}` : '';
};

const useChatMessageReactions = ({
  mode = 'admin',
  scope = 'individual',
  artistId = '',
  eventId = '',
  threadId = '',
  enabled = true,
  setMessages = null,
  onError = null,
} = {}) => {
  const [pendingMap, setPendingMap] = useState({});
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailMessageId, setDetailMessageId] = useState('');
  const [detailGroups, setDetailGroups] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const roomKey = useMemo(
    () => resolveRoomKey({ mode, scope, artistId, eventId, threadId, enabled }),
    [artistId, enabled, eventId, mode, scope, threadId],
  );

  const requestParams = useMemo(
    () => ({ mode, scope, artistId, eventId, threadId }),
    [artistId, eventId, mode, scope, threadId],
  );

  const reportError = useCallback((error, fallbackMessage) => {
    if (error?.name === 'AbortError') return;
    if (typeof onError === 'function') {
      onError(error?.message || fallbackMessage);
    }
  }, [onError]);

  const applySummary = useCallback((messageId, reactions) => {
    if (typeof setMessages !== 'function') return;
    setMessages((prev) => patchMessageReactionSummaries(prev, messageId, reactions));
  }, [setMessages]);

  const refreshMessageReactionDetails = useCallback(async (messageId, { silent = true } = {}) => {
    const normalizedMessageId = normalizeReactionMessageId(messageId);
    if (!roomKey || !normalizedMessageId) return [];
    if (!silent) setDetailLoading(true);
    try {
      const data = await fetchChatMessageReactionDetails({ ...requestParams, messageId: normalizedMessageId });
      const groups = Array.isArray(data?.reactions) ? data.reactions : [];
      applySummary(normalizedMessageId, buildReactionSummaryFromDetailGroups(groups));
      if (detailOpen && normalizedMessageId === detailMessageId) {
        setDetailGroups(groups);
      }
      return groups;
    } catch (error) {
      reportError(error, 'リアクション一覧の取得に失敗しました。');
      return [];
    } finally {
      if (!silent) setDetailLoading(false);
    }
  }, [applySummary, detailMessageId, detailOpen, reportError, requestParams, roomKey]);

  const openReactionDetail = useCallback(async (messageId) => {
    const normalizedMessageId = normalizeReactionMessageId(messageId);
    if (!roomKey || !normalizedMessageId) return;
    setDetailOpen(true);
    setDetailMessageId(normalizedMessageId);
    setDetailLoading(true);
    try {
      const data = await fetchChatMessageReactionDetails({ ...requestParams, messageId: normalizedMessageId });
      const groups = Array.isArray(data?.reactions) ? data.reactions : [];
      setDetailGroups(groups);
      applySummary(normalizedMessageId, buildReactionSummaryFromDetailGroups(groups));
    } catch (error) {
      setDetailOpen(false);
      setDetailMessageId('');
      setDetailGroups([]);
      reportError(error, 'リアクション一覧の取得に失敗しました。');
    } finally {
      setDetailLoading(false);
    }
  }, [applySummary, reportError, requestParams, roomKey]);

  const closeReactionDetail = useCallback(() => {
    setDetailOpen(false);
    setDetailMessageId('');
    setDetailGroups([]);
    setDetailLoading(false);
  }, []);

  const toggleReaction = useCallback(async (messageId, reactionType) => {
    const normalizedMessageId = normalizeReactionMessageId(messageId);
    const normalizedReactionType = String(reactionType || '').trim();
    if (!roomKey || !normalizedMessageId || !normalizedReactionType) return [];
    const pendingKey = `${normalizedMessageId}:${normalizedReactionType}`;
    setPendingMap((prev) => ({ ...prev, [pendingKey]: true }));
    try {
      const data = await postToggleChatMessageReaction({
        ...requestParams,
        messageId: normalizedMessageId,
        reactionType: normalizedReactionType,
      });
      const reactions = Array.isArray(data?.reactions) ? data.reactions : [];
      applySummary(normalizedMessageId, reactions);
      if (detailOpen && normalizedMessageId === detailMessageId) {
        await refreshMessageReactionDetails(normalizedMessageId, { silent: false });
      }
      return reactions;
    } catch (error) {
      reportError(error, 'リアクション更新に失敗しました。');
      throw error;
    } finally {
      setPendingMap((prev) => {
        const next = { ...prev };
        delete next[pendingKey];
        return next;
      });
    }
  }, [applySummary, detailMessageId, detailOpen, refreshMessageReactionDetails, reportError, requestParams, roomKey]);

  const isReactionPending = useCallback((messageId, reactionType = '') => {
    const pendingKey = `${normalizeReactionMessageId(messageId)}:${String(reactionType || '').trim()}`;
    return Boolean(pendingMap[pendingKey]);
  }, [pendingMap]);

  useEffect(() => {
    setPendingMap({});
    closeReactionDetail();
  }, [closeReactionDetail, roomKey]);

  return {
    detailOpen,
    detailMessageId,
    detailGroups,
    detailLoading,
    openReactionDetail,
    closeReactionDetail,
    toggleReaction,
    refreshMessageReactionDetails,
    isReactionPending,
  };
};

export default useChatMessageReactions;
